import { z } from 'zod';
import {
  ASSIGNEE_TYPES,
  CHECK_CADENCES,
  ENERGY_LEVELS,
  NODE_STATUSES,
  NODE_TYPES,
  NOTE_KINDS,
  PERMISSION_ACTIONS,
  PERMISSION_REQUEST_STATUSES,
  REVIEW_TYPES,
  ROUTINE_OCCURRENCE_STATUSES,
  USER_KINDS,
} from './enums';

// ---------- primitives ----------

export const id = z.coerce.number().int().positive();
/** Calendar date, `YYYY-MM-DD`. */
export const isoDate = z.iso.date();
/** Timestamp; accepts ISO strings with offset and returns a Date. */
export const dateTime = z.coerce.date();
export const period = z.string().regex(/^(\d{4}-Q[1-4]|\d{4}-W\d{2}|ongoing)$/, 'period must be YYYY-Qn, YYYY-Wnn or ongoing');
export const context = z.string().regex(/^@[\w-]+$/, 'context must look like @computer');
export const tagName = z.string().trim().toLowerCase().min(1).max(64).regex(/^[\p{L}\p{N}_-]+$/u);

export const nodeType = z.enum(NODE_TYPES);
export const nodeStatus = z.enum(NODE_STATUSES);
export const energy = z.enum(ENERGY_LEVELS);
export const assigneeType = z.enum(ASSIGNEE_TYPES);
export const checkCadence = z.enum(CHECK_CADENCES);
export const userKind = z.enum(USER_KINDS);
export const noteKind = z.enum(NOTE_KINDS);
export const reviewType = z.enum(REVIEW_TYPES);
export const routineOccurrenceStatus = z.enum(ROUTINE_OCCURRENCE_STATUSES);
export const permissionRequestStatus = z.enum(PERMISSION_REQUEST_STATUSES);
export const permissionAction = z.enum(PERMISSION_ACTIONS);
export const priority = z.union([z.literal(1), z.literal(2), z.literal(3)]);

const nullable = <T extends z.ZodType>(s: T) => s.nullable().optional();

// ---------- users and tokens ----------

export const userSchema = z.object({
  id,
  kind: userKind,
  name: z.string().min(1).max(64),
  display_name: z.string().max(128).nullable(),
  role: z.string().max(64).nullable(),
  created_at: dateTime,
});
export type User = z.infer<typeof userSchema>;

export const apiTokenSchema = z.object({
  id,
  user_id: id,
  label: z.string().max(128),
  created_at: dateTime,
  last_used_at: dateTime.nullable(),
  revoked_at: dateTime.nullable(),
});
export type ApiToken = z.infer<typeof apiTokenSchema>;

export const createTokenInput = z.object({
  user_id: id.optional(),
  user_name: z.string().optional(),
  label: z.string().min(1).max(128),
});
export type CreateTokenInput = z.infer<typeof createTokenInput>;

export const loginInput = z.object({
  name: z.string().min(1).default('andreas'),
  password: z.string().min(1),
});

// ---------- nodes ----------

/** Every column of `nodes` (PRD 6.2). */
export const nodeSchema = z.object({
  id,
  type: nodeType,
  parent_id: id.nullable(),
  sort: z.number().int(),
  title: z.string().min(1).max(255),
  description_short: z.string().nullable(),
  status: nodeStatus,
  archived_at: dateTime.nullable(),
  period: z.string().nullable(),
  start_date: isoDate.nullable(),
  due_date: isoDate.nullable(),
  priority: priority.nullable(),
  energy: energy.nullable(),
  effort_min: z.number().int().nullable(),
  context: z.string().nullable(),
  is_next_action: z.boolean(),
  scheduled_at: dateTime.nullable(),
  duration_min: z.number().int().nullable(),
  assignee_type: assigneeType.nullable(),
  assignee_id: id.nullable(),
  buddy_focus: z.boolean(),
  check_cadence: checkCadence.nullable(),
  next_check_at: dateTime.nullable(),
  buddy_note: z.string().max(280).nullable(),
  completion_criterion: z.string().nullable(),
  success_metric: z.string().nullable(),
  target_value: z.number().nullable(),
  current_value: z.number().nullable(),
  evidence: z.string().nullable(),
  needs_clarification: z.boolean(),
  open_question: z.string().nullable(),
  waiting_for: z.string().max(255).nullable(),
  blocked_by_id: id.nullable(),
  recurrence: z.string().nullable(),
  converted_from_type: nodeType.nullable(),
  completed_at: dateTime.nullable(),
  completed_by: id.nullable(),
  created_by: id.nullable(),
  updated_by: id.nullable(),
  created_at: dateTime,
  updated_at: dateTime,
});
export type Node = z.infer<typeof nodeSchema>;

/** Fields a client may set directly. Status changes to done go through /done; archive through /archive. */
const editableNodeFields = {
  title: z.string().trim().min(1).max(255),
  description_short: nullable(z.string()),
  status: nodeStatus,
  period: nullable(period),
  start_date: nullable(isoDate),
  due_date: nullable(isoDate),
  priority: nullable(priority),
  energy: nullable(energy),
  effort_min: nullable(z.number().int().min(0)),
  context: nullable(context),
  is_next_action: z.boolean(),
  scheduled_at: nullable(dateTime),
  duration_min: nullable(z.number().int().min(0)),
  assignee_type: nullable(assigneeType),
  assignee_id: nullable(id),
  check_cadence: nullable(checkCadence),
  next_check_at: nullable(dateTime),
  buddy_note: nullable(z.string().max(280)),
  completion_criterion: nullable(z.string()),
  success_metric: nullable(z.string().max(255)),
  target_value: nullable(z.number()),
  current_value: nullable(z.number()),
  evidence: nullable(z.string()),
  needs_clarification: z.boolean(),
  open_question: nullable(z.string()),
  waiting_for: nullable(z.string().max(255)),
  blocked_by_id: nullable(id),
  recurrence: nullable(z.string().max(255)),
};

export const createNodeInput = z
  .object({
    ...editableNodeFields,
    type: nodeType,
    parent_id: nullable(id),
    sort: z.number().int().optional(),
    buddy_focus: z.boolean().optional(),
    tags: z.array(tagName).optional(),
  })
  .partial()
  .required({ type: true, title: true });
export type CreateNodeInput = z.infer<typeof createNodeInput>;

export const updateNodeInput = z
  .object({ ...editableNodeFields, parent_id: nullable(id) })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export type UpdateNodeInput = z.infer<typeof updateNodeInput>;

const boolParam = z.enum(['true', 'false', '1', '0']).transform((v) => v === 'true' || v === '1');
const csv = <T extends z.ZodType<unknown, string>>(s: T) =>
  z
    .string()
    .transform((v) => v.split(',').filter(Boolean))
    .pipe(z.array(s));

export const nodeListQuery = z.object({
  type: csv(nodeType).optional(),
  parent: z.union([z.literal('none'), id]).optional(),
  status: csv(nodeStatus).optional(),
  assignee: z.string().optional(),
  assignee_type: assigneeType.optional(),
  buddy_focus: boolParam.optional(),
  is_next_action: boolParam.optional(),
  tag: z.string().optional(),
  context: z.string().optional(),
  energy: energy.optional(),
  max_effort: z.coerce.number().int().optional(),
  due_from: isoDate.optional(),
  due_to: isoDate.optional(),
  scheduled_from: dateTime.optional(),
  scheduled_to: dateTime.optional(),
  include_done: boolParam.optional(),
  archived: boolParam.optional(),
  q: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(1000).optional(),
});
export type NodeListQuery = z.infer<typeof nodeListQuery>;

export const moveNodeInput = z.object({
  parent_id: id.nullable(),
  sort: z.number().int().min(0).optional(),
});
export const reorderNodeInput = z.object({ sort: z.number().int().min(0) });
export const convertNodeInput = z.object({ type: z.enum(['task', 'goal', 'result']) });
export const assignNodeInput = z.object({
  assignee_type: assigneeType.nullable(),
  assignee_id: id.nullable().optional(),
  assignee: z.string().optional(),
});
export const buddyInput = z.object({
  buddy_focus: z.boolean(),
  check_cadence: nullable(checkCadence),
  next_check_at: nullable(dateTime),
  buddy_note: nullable(z.string().max(280)),
});
export const buddyCheckedInput = z.object({
  next_check_at: dateTime,
  message: z.string().min(1),
});
export const tagNodeInput = z.object({ tags: z.array(tagName).min(1) });

// ---------- notes ----------

export const noteSchema = z.object({
  id,
  node_id: id,
  kind: noteKind,
  body_md: z.string(),
  version: z.number().int(),
  updated_by: id.nullable(),
  updated_at: dateTime,
});
export type Note = z.infer<typeof noteSchema>;

export const noteVersionSchema = z.object({
  id,
  note_id: id,
  version: z.number().int(),
  body_md: z.string(),
  created_by: id.nullable(),
  created_at: dateTime,
});
export type NoteVersion = z.infer<typeof noteVersionSchema>;

export const putNoteInput = z.object({
  body_md: z.string().max(1_000_000),
  /** The version the edit is based on; 0 for a note that does not exist yet. */
  base_version: z.number().int().min(0),
});

// ---------- tags ----------

export const tagSchema = z.object({ id, name: tagName });
export type Tag = z.infer<typeof tagSchema>;

// ---------- activity ----------

export const activitySchema = z.object({
  id,
  actor_id: id.nullable(),
  action: z.string(),
  node_id: id.nullable(),
  diff: z.unknown().nullable(),
  message: z.string().nullable(),
  created_at: dateTime,
});
export type Activity = z.infer<typeof activitySchema>;

export const logInput = z.object({ message: z.string().min(1).max(10_000), node_id: id.nullable().optional() });
export const activityQuery = z.object({
  actor: z.string().optional(),
  node: id.optional(),
  since: dateTime.optional(),
  kind: userKind.optional(),
  limit: z.coerce.number().int().min(1).max(1000).optional(),
});

// ---------- reviews ----------

export const reviewTemplateQuestion = z.object({ key: z.string(), question: z.string(), hint: z.string().optional() });

export const reviewTemplateSchema = z.object({
  id,
  type: reviewType,
  version: z.number().int(),
  questions: z.array(reviewTemplateQuestion),
  notes: z.string().nullable(),
  created_by: id.nullable(),
  created_at: dateTime,
});
export type ReviewTemplate = z.infer<typeof reviewTemplateSchema>;

export const createReviewTemplateInput = z.object({
  questions: z.array(reviewTemplateQuestion).min(1),
  notes: z.string().optional(),
});

export const reviewSchema = z.object({
  id,
  type: reviewType,
  date: isoDate,
  template_id: id.nullable(),
  answers: z.record(z.string(), z.unknown()),
  summary_md: z.string().nullable(),
  insights_next_time: z.string().nullable(),
  created_by: id.nullable(),
  created_at: dateTime,
});
export type Review = z.infer<typeof reviewSchema>;

export const createReviewInput = z.object({
  type: reviewType,
  date: isoDate,
  template_id: id.optional(),
  answers: z.record(z.string(), z.unknown()),
  summary_md: z.string().optional(),
  insights_next_time: z.string().optional(),
  focus_node_ids: z.array(id).optional(),
});

// ---------- agent sessions ----------

export const agentSessionSchema = z.object({
  id,
  user_id: id,
  summary_md: z.string(),
  decisions_md: z.string().nullable(),
  node_ids: z.array(id),
  started_at: dateTime.nullable(),
  created_at: dateTime,
});
export type AgentSession = z.infer<typeof agentSessionSchema>;

export const createAgentSessionInput = z.object({
  summary_md: z.string().min(1),
  decisions_md: z.string().optional(),
  node_ids: z.array(id).default([]),
  started_at: dateTime.optional(),
});

// ---------- saved views ----------

export const savedViewSchema = z.object({
  id,
  name: z.string().min(1).max(128),
  filter: z.record(z.string(), z.unknown()),
  sort: z.string().nullable(),
  icon: z.string().nullable(),
  pinned: z.boolean(),
  position: z.number().int(),
  created_by: id.nullable(),
  created_at: dateTime,
});
export type SavedView = z.infer<typeof savedViewSchema>;

export const createSavedViewInput = z.object({
  name: z.string().min(1).max(128),
  filter: z.record(z.string(), z.unknown()),
  sort: z.string().optional(),
  icon: z.string().optional(),
  pinned: z.boolean().default(true),
  position: z.number().int().optional(),
});
export const updateSavedViewInput = createSavedViewInput.partial();

// ---------- daily focus ----------

export const dailyFocusSchema = z.object({
  id,
  date: isoDate,
  node_id: id,
  rank: z.number().int(),
  suggested_by: id.nullable(),
  confirmed: z.boolean(),
  created_at: dateTime,
});
export type DailyFocus = z.infer<typeof dailyFocusSchema>;

export const putFocusInput = z.object({
  date: isoDate,
  node_ids: z.array(id),
  confirmed: z.boolean().default(true),
});
export const suggestFocusInput = z.object({ date: isoDate.optional(), save: z.boolean().default(false) });

// ---------- ritual schedules ----------

export const ritualScheduleSchema = z.object({
  id,
  type: reviewType,
  rrule: z.string(),
  time: z.string().regex(/^\d{2}:\d{2}$/),
  timezone: z.string(),
  next_run_at: dateTime.nullable(),
  enabled: z.boolean(),
  updated_at: dateTime,
});
export type RitualSchedule = z.infer<typeof ritualScheduleSchema>;

export const updateScheduleInput = z
  .object({
    rrule: z.string().min(1),
    time: z.string().regex(/^\d{2}:\d{2}$/),
    timezone: z.string().min(1),
    next_run_at: dateTime.nullable(),
    enabled: z.boolean(),
  })
  .partial();

// ---------- settings ----------

export const settingsSchema = z.object({
  focus_count: z.coerce.number().int().min(1).max(20),
  buddy_focus_limit: z.coerce.number().int().min(1).max(50),
  timezone: z.string().min(1),
  stale_task_days: z.coerce.number().int().min(1).max(365),
});
export type Settings = z.infer<typeof settingsSchema>;
export const DEFAULT_SETTINGS: Settings = {
  focus_count: 5,
  buddy_focus_limit: 5,
  timezone: 'Europe/Berlin',
  stale_task_days: 14,
};
export const updateSettingsInput = settingsSchema.partial();

// ---------- routines ----------

export const routineOccurrenceSchema = z.object({
  id: id.nullable(),
  routine_id: id,
  date: isoDate,
  status: routineOccurrenceStatus,
  completed_at: dateTime.nullable(),
  note: z.string().nullable(),
});
export type RoutineOccurrence = z.infer<typeof routineOccurrenceSchema>;

export const occurrenceQuery = z.object({ from: isoDate.optional(), to: isoDate.optional() });
export const occurrenceStatusInput = z.object({ note: z.string().optional() });

// ---------- permission requests ----------

export const permissionRequestSchema = z.object({
  id,
  requested_by: id,
  node_id: id.nullable(),
  action: permissionAction,
  payload: z.unknown(),
  reason: z.string().nullable(),
  status: permissionRequestStatus,
  decided_by: id.nullable(),
  decided_at: dateTime.nullable(),
  decision_note: z.string().nullable(),
  created_at: dateTime,
});
export type PermissionRequest = z.infer<typeof permissionRequestSchema>;

export const createPermissionInput = z.object({
  node_id: id.nullable().optional(),
  action: permissionAction,
  payload: z.unknown().default({}),
  reason: z.string().optional(),
});
export const decidePermissionInput = z.object({ note: z.string().optional() });

// ---------- errors ----------

export const apiErrorSchema = z.object({
  error: z.object({ code: z.string(), message: z.string(), details: z.unknown().optional() }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;
